import {
  ONBOARDING_TEMPLATE_EXCHANGE_MAX_BYTES,
  type OnboardingAssigneeKind,
  type OnboardingTemplateImportAssignment,
  type OnboardingTemplateImportPreview,
  type OnboardingTemplateImportPreviewReference,
  type OnboardingTemplateImportPreviewTask,
  type OrgUnit,
  type RoleOption,
  type TeamSummary,
  type OnboardingAssigneeUser,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { mergeOrgOptions } from '@/platform/features/users/org-select-options';
import { ONBOARDING_DATA_FIELD_TYPE_LABEL } from '@/products/onboarding/features/onboarding-task-field-utils';
import {
  ONBOARDING_ASSIGNEE_KIND_LABEL,
  ONBOARDING_CASE_KIND_LABEL,
  ONBOARDING_DUE_ANCHOR_LABEL,
  ONBOARDING_TASK_KIND_LABEL,
} from '@/products/onboarding/features/onboarding-template-labels';
import { useOnboardingAssigneeOptions } from '@/products/onboarding/features/use-onboarding-assignee-options';
import {
  useCommitOnboardingTemplateImport,
  usePreviewOnboardingTemplateImport,
} from '@/products/onboarding/features/use-onboarding-templates';

const EMPTY_FILE = 'Bitte eine JSON-Datei auswählen.';
const IMPORT_FAILED = 'Die Vorlage konnte nicht importiert werden.';
const PREVIEW_FAILED = 'Die Vorlage konnte nicht geprüft werden.';

type AssignmentChoice = {
  assigneeUserId: string | null;
  assigneeRoleId: string | null;
  assigneeDepartmentId: string | null;
  assigneeTeamId: string | null;
};

export function OnboardingTemplateImportDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: (templateId: string) => void;
}) {
  const copy = useVoiceCopy();
  const previewMutation = usePreviewOnboardingTemplateImport();
  const commitMutation = useCommitOnboardingTemplateImport();
  const [error, setError] = useState<string | null>(null);
  const [document, setDocument] = useState<unknown>(null);
  const [preview, setPreview] = useState<OnboardingTemplateImportPreview | null>(null);
  const [name, setName] = useState('');
  const [assignments, setAssignments] = useState<Record<string, AssignmentChoice>>({});
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [personQ, setPersonQ] = useState('');
  const [fileKey, setFileKey] = useState(0);

  const needsPick = Boolean(preview && concreteTasks(preview).length > 0);
  const needsPersonSearch = Boolean(
    preview?.phases.some((phase) => phase.tasks.some((task) => task.assignee === 'user')),
  );
  const options = useOnboardingAssigneeOptions(
    {
      page: 1,
      pageSize: 50,
      includeInactive: false,
      q: needsPersonSearch ? personQ || undefined : undefined,
    },
    { enabled: open && needsPick },
  );

  function reset() {
    setError(null);
    setDocument(null);
    setPreview(null);
    setName('');
    setAssignments({});
    setIdempotencyKey(null);
    setPersonQ('');
    setFileKey((current) => current + 1);
  }

  function closeDialog(next: boolean) {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  async function onFileChange(file: File | undefined) {
    setError(null);
    setDocument(null);
    setPreview(null);
    setName('');
    setAssignments({});
    setIdempotencyKey(null);
    try {
      const read = await readImportFile(file);
      if (!read.ok) {
        setError(read.message);
        return;
      }
      const key = crypto.randomUUID();
      setIdempotencyKey(key);
      setDocument(read.document);
      const next = await previewMutation.mutateAsync(read.document);
      setPreview(next);
      setName(next.suggestedName);
      setAssignments(suggestedAssignments(next));
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : PREVIEW_FAILED);
    }
  }

  async function onConfirm() {
    if (!document || !preview || !idempotencyKey) {
      return;
    }
    setError(null);
    try {
      const created = await commitMutation.mutateAsync({
        document,
        name: name.trim(),
        createIdempotencyKey: idempotencyKey,
        assignments: toCommitAssignments(preview, assignments),
      });
      reset();
      onImported(created.id);
    } catch (caught) {
      const message = caught instanceof ApiRequestError ? caught.message : IMPORT_FAILED;
      setError(message);
      toast.error(message);
    }
  }

  const confirmDisabled =
    !preview ||
    !document ||
    !idempotencyKey ||
    name.trim() === '' ||
    previewMutation.isPending ||
    commitMutation.isPending ||
    hasMissingAssignment(preview, assignments);

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent className="flex max-h-[min(90vh,48rem)] flex-col overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Vorlage importieren</DialogTitle>
          <DialogDescription>{copy('onboarding.template.import.hint')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="onboarding-tpl-import-file">Datei auswählen</Label>
            <Input
              key={fileKey}
              id="onboarding-tpl-import-file"
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                void onFileChange(file);
              }}
            />
          </div>

          {previewMutation.isPending ? (
            <p className="text-sm text-muted-foreground">Datei wird geprüft …</p>
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          {preview ? (
            <PreviewBody
              preview={preview}
              name={name}
              onNameChange={setName}
              assignments={assignments}
              onAssignmentChange={(taskKey, patch) =>
                setAssignments((current) => ({
                  ...current,
                  [taskKey]: { ...emptyChoice(), ...current[taskKey], ...patch },
                }))
              }
              personQ={personQ}
              onPersonQChange={setPersonQ}
              needsPersonSearch={needsPersonSearch}
              optionsPending={options.isPending}
              optionsError={
                options.isError
                  ? options.error instanceof ApiRequestError
                    ? options.error.message
                    : 'Die Auswahl konnte nicht geladen werden.'
                  : null
              }
              people={options.data?.users.items ?? []}
              roles={options.data?.roles ?? []}
              departments={options.data?.departments ?? []}
              teams={options.data?.teams ?? []}
            />
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => closeDialog(false)}>
            Abbrechen
          </Button>
          <Button type="button" onClick={() => void onConfirm()} disabled={confirmDisabled}>
            Importieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewBody({
  preview,
  name,
  onNameChange,
  assignments,
  onAssignmentChange,
  personQ,
  onPersonQChange,
  needsPersonSearch,
  optionsPending,
  optionsError,
  people,
  roles,
  departments,
  teams,
}: {
  preview: OnboardingTemplateImportPreview;
  name: string;
  onNameChange: (name: string) => void;
  assignments: Record<string, AssignmentChoice>;
  onAssignmentChange: (taskKey: string, patch: Partial<AssignmentChoice>) => void;
  personQ: string;
  onPersonQChange: (value: string) => void;
  needsPersonSearch: boolean;
  optionsPending: boolean;
  optionsError: string | null;
  people: OnboardingAssigneeUser[];
  roles: RoleOption[];
  departments: OrgUnit[];
  teams: TeamSummary[];
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {ONBOARDING_CASE_KIND_LABEL[preview.kind]}
        {preview.description ? ` · ${preview.description}` : ''}
      </p>

      <div className="space-y-2">
        <Label htmlFor="onboarding-tpl-import-name">Name</Label>
        <Input
          id="onboarding-tpl-import-name"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
        />
        {preview.nameConflict ? (
          <p className="text-sm text-muted-foreground">
            Eine Vorlage mit diesem Namen ist bereits vorhanden. Vorgeschlagen:{' '}
            {preview.suggestedName}
          </p>
        ) : null}
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Vorschau</h3>
        {needsPersonSearch ? (
          <div className="space-y-2">
            <Label htmlFor="onboarding-tpl-import-person">Person suchen</Label>
            <Input
              id="onboarding-tpl-import-person"
              value={personQ}
              onChange={(event) => onPersonQChange(event.target.value)}
              placeholder="Suche nach Name oder E-Mail"
            />
          </div>
        ) : null}
        {optionsPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
        {optionsError ? (
          <p className="text-sm text-destructive" role="alert">
            {optionsError}
          </p>
        ) : null}
        {preview.phases.map((phase) => (
          <section
            key={phase.id}
            className="space-y-3 rounded-xl border border-border bg-card p-4 text-card-foreground shadow-sm"
          >
            <h4 className="text-sm font-medium">
              {phase.title}
              <span className="font-normal text-muted-foreground">
                {' '}
                · Reihenfolge {phase.sortOrder}
              </span>
            </h4>
            {phase.tasks.map((task) => (
              <article key={task.key} className="space-y-2 rounded-md bg-muted/40 p-3">
                <p className="font-medium">{task.title}</p>
                <p className="text-sm text-muted-foreground">
                  {ONBOARDING_TASK_KIND_LABEL[task.kind]}
                  {task.required ? ' · Pflicht' : ' · optional'}
                  {` · ${ONBOARDING_DUE_ANCHOR_LABEL[task.anchor]}`}
                  {task.offsetDays !== 0 ? ` · ${task.offsetDays} Tage` : ''}
                  {task.visibleToSubject ? ' · für betroffene Person sichtbar' : ''}
                </p>
                {task.instructions ? (
                  <p className="text-sm whitespace-pre-wrap">{task.instructions}</p>
                ) : null}
                {task.dependsOn.length > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Hängt ab von: {task.dependsOn.join(', ')}
                  </p>
                ) : null}
                {task.dataFields.length > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Felder:{' '}
                    {task.dataFields
                      .map(
                        (field) =>
                          `${field.label} (${ONBOARDING_DATA_FIELD_TYPE_LABEL[field.type]})`,
                      )
                      .join(', ')}
                  </p>
                ) : null}
                {isConcreteAssignee(task.assignee) ? (
                  <AssigneeField
                    task={task}
                    choice={assignments[task.key] ?? emptyChoice()}
                    onChange={(patch) => onAssignmentChange(task.key, patch)}
                    people={people}
                    roles={roles}
                    departments={departments}
                    teams={teams}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Zuständigkeit: {ONBOARDING_ASSIGNEE_KIND_LABEL[task.assignee]}
                  </p>
                )}
              </article>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

function AssigneeField({
  task,
  choice,
  onChange,
  people,
  roles,
  departments,
  teams,
}: {
  task: OnboardingTemplateImportPreviewTask;
  choice: AssignmentChoice;
  onChange: (patch: Partial<AssignmentChoice>) => void;
  people: OnboardingAssigneeUser[];
  roles: RoleOption[];
  departments: OrgUnit[];
  teams: TeamSummary[];
}) {
  const status = referenceStatusLabel(task.reference);
  const currentPeople = ensureCurrentPerson(people, choice.assigneeUserId, task.reference.label);
  const currentDepartments = mergeOrgOptions(
    departments,
    currentOrg(departments, choice.assigneeDepartmentId),
  );
  const currentTeams = mergeOrgOptions(teams, currentOrg(teams, choice.assigneeTeamId));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <Label>{ONBOARDING_ASSIGNEE_KIND_LABEL[task.assignee]}</Label>
        {status ? <span className="text-sm text-muted-foreground">{status}</span> : null}
        {task.reference.status === 'inactive' && task.reference.label ? (
          <span className="text-sm text-muted-foreground">({task.reference.label})</span>
        ) : null}
      </div>
      {task.assignee === 'user' ? (
        <Select
          value={choice.assigneeUserId ?? undefined}
          onValueChange={(value) => onChange({ assigneeUserId: value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Bitte zuordnen" />
          </SelectTrigger>
          <SelectContent>
            {currentPeople.length === 0 ? (
              <SelectItem value="__empty" disabled>
                Keine Treffer.
              </SelectItem>
            ) : (
              currentPeople.map((person) => (
                <SelectItem key={person.id} value={person.id}>
                  {person.displayName}
                  {person.email ? ` · ${person.email}` : ''}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      ) : null}
      {task.assignee === 'role' ? (
        <Select
          value={choice.assigneeRoleId ?? undefined}
          onValueChange={(value) => onChange({ assigneeRoleId: value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Bitte zuordnen" />
          </SelectTrigger>
          <SelectContent>
            {roles.length === 0 ? (
              <SelectItem value="__empty" disabled>
                Keine Treffer.
              </SelectItem>
            ) : (
              roles.map((role) => (
                <SelectItem key={role.id} value={role.id}>
                  {roleLabel(role, roles)}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      ) : null}
      {task.assignee === 'department' ? (
        <Select
          value={choice.assigneeDepartmentId ?? undefined}
          onValueChange={(value) => onChange({ assigneeDepartmentId: value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Bitte zuordnen" />
          </SelectTrigger>
          <SelectContent>
            {currentDepartments.map((department) => (
              <SelectItem key={department.id} value={department.id}>
                {orgLabel(department, currentDepartments)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      {task.assignee === 'team' ? (
        <Select
          value={choice.assigneeTeamId ?? undefined}
          onValueChange={(value) => onChange({ assigneeTeamId: value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Bitte zuordnen" />
          </SelectTrigger>
          <SelectContent>
            {currentTeams.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {orgLabel(team, currentTeams)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
    </div>
  );
}

async function readImportFile(
  file: File | undefined,
): Promise<{ ok: true; document: unknown } | { ok: false; message: string }> {
  if (!file) {
    return { ok: false, message: EMPTY_FILE };
  }
  if (file.size > ONBOARDING_TEMPLATE_EXCHANGE_MAX_BYTES) {
    return { ok: false, message: 'Die Datei ist zu groß.' };
  }
  const text = await readFileAsText(file);
  if (text.trim() === '') {
    return { ok: false, message: EMPTY_FILE };
  }
  if (new TextEncoder().encode(text).length > ONBOARDING_TEMPLATE_EXCHANGE_MAX_BYTES) {
    return { ok: false, message: 'Die Datei ist zu groß.' };
  }
  try {
    return { ok: true, document: JSON.parse(text) as unknown };
  } catch {
    return { ok: false, message: 'Die Datei ist keine Onboarding-Vorlage.' };
  }
}

function readFileAsText(file: File): Promise<string> {
  if (typeof file.text === 'function') {
    return file.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () =>
      reject(reader.error ?? new Error('Die Datei konnte nicht gelesen werden.'));
    reader.readAsText(file);
  });
}

function suggestedAssignments(
  preview: OnboardingTemplateImportPreview,
): Record<string, AssignmentChoice> {
  const next: Record<string, AssignmentChoice> = {};
  for (const task of concreteTasks(preview)) {
    const suggested = task.reference.status === 'suggested' ? task.reference.suggestedId : null;
    next[task.key] = {
      assigneeUserId: task.assignee === 'user' ? suggested : null,
      assigneeRoleId: task.assignee === 'role' ? suggested : null,
      assigneeDepartmentId: task.assignee === 'department' ? suggested : null,
      assigneeTeamId: task.assignee === 'team' ? suggested : null,
    };
  }
  return next;
}

function toCommitAssignments(
  preview: OnboardingTemplateImportPreview,
  selected: Record<string, AssignmentChoice>,
): OnboardingTemplateImportAssignment[] {
  return concreteTasks(preview).map((task) => {
    const choice = selected[task.key] ?? emptyChoice();
    return {
      taskKey: task.key,
      assigneeUserId: task.assignee === 'user' ? choice.assigneeUserId : null,
      assigneeRoleId: task.assignee === 'role' ? choice.assigneeRoleId : null,
      assigneeDepartmentId: task.assignee === 'department' ? choice.assigneeDepartmentId : null,
      assigneeTeamId: task.assignee === 'team' ? choice.assigneeTeamId : null,
    };
  });
}

function hasMissingAssignment(
  preview: OnboardingTemplateImportPreview,
  selected: Record<string, AssignmentChoice>,
): boolean {
  return concreteTasks(preview).some((task) => {
    const choice = selected[task.key] ?? emptyChoice();
    if (task.assignee === 'user') {
      return !choice.assigneeUserId;
    }
    if (task.assignee === 'role') {
      return !choice.assigneeRoleId;
    }
    if (task.assignee === 'department') {
      return !choice.assigneeDepartmentId;
    }
    return !choice.assigneeTeamId;
  });
}

function concreteTasks(preview: OnboardingTemplateImportPreview) {
  return preview.phases.flatMap((phase) =>
    phase.tasks.filter((task) => isConcreteAssignee(task.assignee)),
  );
}

function isConcreteAssignee(
  assignee: OnboardingAssigneeKind,
): assignee is 'user' | 'role' | 'department' | 'team' {
  return (
    assignee === 'user' || assignee === 'role' || assignee === 'department' || assignee === 'team'
  );
}

function referenceStatusLabel(reference: OnboardingTemplateImportPreviewReference): string | null {
  if (reference.status === 'suggested') {
    return 'Vorschlag';
  }
  if (reference.status === 'missing') {
    return 'Nicht gefunden';
  }
  if (reference.status === 'inactive') {
    return 'Bitte zuordnen';
  }
  return null;
}

function emptyChoice(): AssignmentChoice {
  return {
    assigneeUserId: null,
    assigneeRoleId: null,
    assigneeDepartmentId: null,
    assigneeTeamId: null,
  };
}

function currentOrg<T extends OrgUnit | TeamSummary>(
  list: T[],
  id: string | null | undefined,
): T | null {
  if (!id) {
    return null;
  }
  return list.find((item) => item.id === id) ?? null;
}

function orgLabel(item: { name: string; slug: string }, all: { name: string; slug: string }[]) {
  const duplicates = all.filter((entry) => entry.name === item.name).length > 1;
  return duplicates ? `${item.name} (${item.slug})` : item.name;
}

function roleLabel(role: RoleOption, roles: RoleOption[]) {
  const duplicates = roles.filter((entry) => entry.name === role.name).length > 1;
  return duplicates ? `${role.name} (${role.code})` : role.name;
}

function ensureCurrentPerson(
  items: OnboardingAssigneeUser[],
  currentId: string | null | undefined,
  label: string | null,
): OnboardingAssigneeUser[] {
  if (!currentId) {
    return items;
  }
  if (items.some((item) => item.id === currentId)) {
    return items;
  }
  return [
    {
      id: currentId,
      email: '',
      displayName: label ?? currentId,
      isActive: true,
    },
    ...items,
  ];
}
